'use client'

import { Button } from 'design-system'
import { ChevronLeft } from 'lucide-react'
import { FC, useId, useState } from 'react'

import { BooleanSubFilter } from './boolean-sub-filter'
import { DateRangeSubFilter } from './date-range-sub-filter'
import { NumberRangeSubFilter } from './number-range-sub-filter'
import { SearchSubFilter } from './search-sub-filter'
import { SelectSubFilter } from './select-sub-filter'
import { TreeSubFilter } from './tree-sub-filter'
import { DateRangeFilterConfig, FilterConfig, FilterValues } from './types'

type SubFilterControlProps = {
  filter: FilterConfig
  values: FilterValues
  onChange: (key: string, value: unknown) => void
  /**
   * For date-range filters, whose value is a list of OR'd ranges: edit the
   * range carrying this id (an active-filter pill editing itself). When
   * absent, the editing session appends a new range instead.
   */
  dateRangeId?: string
}

/** @public */
export type DateRangeValue = {
  /**
   * Stable client-side identity for the range within its filter's list —
   * lets pills key off it and editors track their range across removals of
   * earlier ranges (indexes shift; ids don't).
   */
  id?: string
  start?: Date
  end?: Date
}

// Date filters hold several OR'd ranges (YD-12308). The edited range is
// tracked by id, fixed on mount: with `editRangeId` the editor works on that
// one existing range; without it (the Filter menu) the session's first change
// appends a new range and later changes refine it. Each popover open remounts
// the control, starting a fresh range. If the session's range is removed from
// under the editor (both bounds cleared), the editor empties and the next
// change appends rather than overwriting a neighbor.
const MultiDateRangeSubFilter: FC<{
  filter: DateRangeFilterConfig
  ranges: DateRangeValue[]
  editRangeId?: string
  onChange: (next: DateRangeValue[]) => void
}> = ({ filter, ranges, editRangeId, onChange }) => {
  const sessionId = useId()
  const [editId] = useState(() => editRangeId ?? sessionId)
  const index = ranges.findIndex(range => range.id === editId)

  const handleChange = (range: DateRangeValue) => {
    const stamped = { ...range, id: editId }
    const next = [...ranges]
    if (index >= 0) {
      next[index] = stamped
    } else {
      next.push(stamped)
    }
    onChange(next)
  }

  return (
    <DateRangeSubFilter
      label={filter.label}
      value={index >= 0 ? ranges[index] : undefined}
      onChange={handleChange}
    />
  )
}

type SubFilterViewProps = SubFilterControlProps & {
  onBack: () => void
}

// The bare type-switched filter editor, shared by the Filter dropdown's
// sub-filter view and inline editing from an active-filter chip.
export const SubFilterControl: FC<SubFilterControlProps> = ({
  filter,
  values,
  onChange,
  dateRangeId
}) => {
  const handleChange = (value: unknown) => onChange(filter.key, value)

  return (
    <>
      {filter.type === 'select' && (
        <SelectSubFilter
          config={filter}
          value={(values[filter.key] as string[]) ?? []}
          onChange={handleChange}
        />
      )}
      {filter.type === 'search' && (
        <SearchSubFilter
          config={filter}
          value={(values[filter.key] as string[]) ?? []}
          onChange={handleChange}
        />
      )}
      {filter.type === 'tree' && (
        <TreeSubFilter
          config={filter}
          value={(values[filter.key] as string[]) ?? []}
          onChange={handleChange}
        />
      )}
      {filter.type === 'date-range' && (
        <MultiDateRangeSubFilter
          filter={filter}
          ranges={(values[filter.key] as DateRangeValue[] | undefined) ?? []}
          editRangeId={dateRangeId}
          onChange={handleChange}
        />
      )}
      {filter.type === 'boolean' && (
        <BooleanSubFilter
          config={filter}
          value={values[filter.key] as boolean | undefined}
          onChange={handleChange}
        />
      )}
      {filter.type === 'number-range' && (
        <NumberRangeSubFilter
          label={filter.label}
          value={
            values[filter.key] as { min?: number; max?: number } | undefined
          }
          onChange={handleChange}
        />
      )}
    </>
  )
}

export const SubFilterView: FC<SubFilterViewProps> = ({
  filter,
  values,
  onChange,
  onBack
}) => (
  <section aria-label={filter.label}>
    <header className="flex items-center gap-2 p-2">
      <Button
        buttonType="gray"
        buttonSize="sm"
        leftIconSlot={<ChevronLeft />}
        onClick={onBack}
        aria-label="Back to filter list">
        Back
      </Button>
      <span className="text-sm font-semibold">{filter.label}</span>
    </header>
    <SubFilterControl filter={filter} values={values} onChange={onChange} />
  </section>
)
